import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useState, useCallback } from "react";
import { useGenerateConfig, useReloadConfig } from "@/hooks/use-config";
import { toast } from "sonner";
import {
  Cable,
  Shield,
  Radio,
  Fingerprint,
  Layers,
  PhoneCall,
  Webhook,
  Globe,
  Wand2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileCode,
  KeyRound,
  Terminal,
  ScrollText,
} from "lucide-react";

interface SidebarProps {
  section: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const configNav: NavGroup[] = [
  {
    label: "PJSIP",
    items: [
      { label: "Trunk Contexts", path: "/config/contexts", icon: <Layers size={16} /> },
      { label: "Transports", path: "/config/transports", icon: <Cable size={16} /> },
      { label: "Auths", path: "/config/auths", icon: <Shield size={16} /> },
      { label: "AORs", path: "/config/aors", icon: <Radio size={16} /> },
      { label: "Identifies", path: "/config/identifies", icon: <Fingerprint size={16} /> },
    ],
  },
  {
    label: "Dialplan",
    items: [
      { label: "Extensions", path: "/config/extensions", icon: <PhoneCall size={16} /> },
    ],
  },
  {
    label: "Applications",
    items: [
      { label: "ARI Apps", path: "/config/ari-apps", icon: <Webhook size={16} /> },
      { label: "WebSocket", path: "/config/websocket-endpoints", icon: <Globe size={16} /> },
    ],
  },
];

const adminNav: NavGroup[] = [
  {
    label: "Tools",
    items: [
      { label: "AMI Console", path: "/admin/ami-console", icon: <Terminal size={16} /> },
    ],
  },
  {
    label: "System",
    items: [
      { label: "Server Logs", path: "/admin/logs", icon: <ScrollText size={16} /> },
      { label: "Generated Config", path: "/admin/config", icon: <FileCode size={16} /> },
      { label: "MCP API Keys", path: "/admin/api-keys", icon: <KeyRound size={16} /> },
    ],
  },
  {
    label: "Audit",
    items: [
      { label: "Audit Logs", path: "/admin/audit-logs", icon: <ClipboardList size={16} /> },
    ],
  },
];

export function Sidebar({ section }: SidebarProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const generate = useGenerateConfig();
  const reload = useReloadConfig();

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const handleGenerate = () => {
    generate.mutate("all", {
      onSuccess: (data) => toast.success(`Config generated: ${Object.keys(data.results).join(", ")}`),
      onError: () => toast.error("Config generation failed"),
    });
  };

  const handleReload = () => {
    reload.mutate("all", {
      onSuccess: () => toast.success("Config reloaded"),
      onError: () => toast.error("Config reload failed"),
    });
  };

  const nav = section === "config" ? configNav : section === "admin" ? adminNav : [];

  return (
    <aside className="flex flex-col w-56 h-full bg-sidebar border-r border-border">
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-4 scrollbar-thin">
        {nav.map((group) => {
          const collapsed = collapsedGroups.has(group.label);
          return (
            <div key={group.label} className="space-y-1">
              <button
                onClick={() => toggleGroup(group.label)}
                className="flex items-center gap-1.5 w-full text-xs font-semibold text-sidebar-foreground uppercase tracking-wider px-1 py-0.5 hover:text-foreground transition-colors"
              >
                {collapsed ? (
                  <ChevronRight size={12} />
                ) : (
                  <ChevronDown size={12} />
                )}
                {group.label}
              </button>
              {!collapsed && (
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-sidebar-active/15 text-sidebar-active font-medium"
                            : "text-sidebar-foreground hover:text-foreground hover:bg-muted/50"
                        )
                      }
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Separator />
      <div className="p-3 space-y-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleGenerate}
          disabled={generate.isPending}
          className="w-full h-8 text-xs gap-1.5"
          title="Generate all config files"
        >
          <Wand2 size={14} />
          Generate All Config
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleReload}
          disabled={reload.isPending}
          className="w-full h-8 text-xs gap-1.5"
          title="Reload all asterisk modules"
        >
          <RefreshCw size={14} />
          Reload All Config
        </Button>
      </div>
    </aside>
  );
}
